import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import axios from 'axios';
import type { CancelTokenSource } from 'axios';

import { logger } from 'packages/logger';
import { useUrlLocalizer } from 'tradera/lang/translate';
import {
    defaultJsonRequestHeaders,
    handleError,
    API_ERRORS,
    reloadOnUnauthorized
} from 'tradera/utils/api';
import * as itemActions from 'tradera/state/items/actions';
import uiActions from 'tradera/state/ui/actions';
import { ItemCard } from 'tradera/components/item-card/item-card';
import type { PossibleItemTypes } from 'tradera/components/item-card/item-card';
import { ComponentsWithStitchAds } from 'tradera/apps/tradera/components/discover/components/search-results/items-with-stiched-ads';
import { useIsFeatureEnabled } from 'tradera/hooks/use-is-feature-enabled';
import { useAppDispatch } from 'tradera/state/hooks';
import { LocalLoader } from '../loader/loader';

const LOADING_STATES = {
    // can transition to is represented by '->'
    initializing: 'initializing', // -> loading
    loading: 'loading', // -> ready, end, empty
    ready: 'ready', // -> loading
    end: 'end', // final state
    empty: 'empty' // final state
};

type RecommendationsProps = {
    createRequestUrl: (pageIndex: number) => string;
    errorFallback?: React.ReactNode;
    gtmLabel: string;
    itemCardProps?: { showSellerAlias?: boolean };
    ssrItems?: PossibleItemTypes[] | null;
    noMoreItemsMarker?: React.ReactNode;
    onReachedEnd?: (itemIds: number[]) => void;
    showAds?: boolean;
};

export const Recommendations = ({
    createRequestUrl,
    errorFallback,
    gtmLabel,
    itemCardProps,
    ssrItems,
    noMoreItemsMarker,
    onReachedEnd
}: RecommendationsProps) => {
    const showAds = useIsFeatureEnabled('show-ads-for-recommendation-pages');
    const enableQuickView = useIsFeatureEnabled('quick-view');
    const dispatch = useAppDispatch();
    const toggleWishlist = (itemId: number) =>
        dispatch(itemActions.wishlist(itemId));
    const [loadingState, setLoadingState] = useState(
        ssrItems
            ? ssrItems.length > 0
                ? LOADING_STATES.ready
                : LOADING_STATES.empty
            : LOADING_STATES.initializing
    );
    const [currentPage, setCurrentPage] = useState(0);
    const [items, setItems] = useState<PossibleItemTypes[]>(ssrItems || []);
    const { toLocalizedUrl } = useUrlLocalizer();

    const [loadingTriggerRef, loadingTriggerInView] = useInView({
        rootMargin: '0px 0px 800px 0px', // Increase to load earlier
        threshold: 1.0
    });

    const addItems = useCallback(
        (itemsToAdd: PossibleItemTypes[]) => {
            setItems([...items, ...itemsToAdd]);
        },
        [items, setItems]
    );

    useEffect(() => {
        if (ssrItems) {
            setItems(ssrItems);
            if (ssrItems.length > 0) {
                setLoadingState(LOADING_STATES.ready);
            } else {
                setLoadingState(LOADING_STATES.empty);
            }
        }
    }, [ssrItems]);

    // For cancelling request when unmounting component:
    const cancelToken = useRef<CancelTokenSource>();
    useEffect(() => {
        cancelToken.current = axios.CancelToken.source();
        return cancelToken.current.cancel;
    }, []);

    const getItems = useCallback(
        (page: number) => {
            const endpoint = toLocalizedUrl(createRequestUrl(page));
            axios
                .get(endpoint, {
                    cancelToken: cancelToken.current?.token,
                    headers: defaultJsonRequestHeaders
                })
                .then(({ data }) => {
                    const itemsInResponse = data?.items || [];
                    if (itemsInResponse.length === 0) {
                        if (items.length === 0) {
                            setLoadingState(LOADING_STATES.empty);
                        } else {
                            setLoadingState(LOADING_STATES.end);
                            if (onReachedEnd)
                                onReachedEnd(items.map((x) => x.itemId));
                        }
                    } else {
                        addItems(itemsInResponse);
                        setLoadingState(LOADING_STATES.ready);
                    }
                    return itemsInResponse;
                })
                .catch(handleError())
                .catch(reloadOnUnauthorized)
                .catch((error) => {
                    switch (error.message) {
                        case API_ERRORS.CANCELLED:
                            return;
                        case API_ERRORS.ABORTED:
                        case API_ERRORS.NETWORK:
                            break;
                        default:
                            logger(error);
                    }
                    if (items.length === 0) {
                        setLoadingState(LOADING_STATES.empty);
                    } else {
                        setLoadingState(LOADING_STATES.end);
                    }
                });
        },
        [toLocalizedUrl, createRequestUrl, items, onReachedEnd, addItems]
    );

    useEffect(() => {
        if (
            loadingState === LOADING_STATES.initializing ||
            (loadingState === LOADING_STATES.ready &&
                loadingTriggerInView &&
                !ssrItems)
        ) {
            setLoadingState(LOADING_STATES.loading);
            const nextPage = currentPage + 1;
            setCurrentPage(nextPage);
            return getItems(nextPage);
        }
    }, [currentPage, getItems, loadingState, loadingTriggerInView, ssrItems]);

    const handleQuickViewClick = useCallback(
        (item: PossibleItemTypes) =>
            dispatch(uiActions.openQuickViewAction({ items, item, gtmLabel })),
        [gtmLabel, items, dispatch]
    );

    const itemComponents = items.map((item, index) => (
        <div key={index} className="item-card-container flex-shrink-0">
            <ItemCard
                item={item}
                onAddToWishlist={toggleWishlist}
                onQuickView={enableQuickView ? handleQuickViewClick : undefined}
                onRemoveFromWishlist={toggleWishlist}
                {...itemCardProps}
            />
        </div>
    ));

    const loader = (
        <div className="position-relative" style={{ minHeight: '50vh' }}>
            <LocalLoader />
        </div>
    );

    const loadingTrigger = (
        <div
            ref={loadingTriggerRef}
            style={{
                height: 1,
                width: 1,
                margin: 1,
                display:
                    loadingState === LOADING_STATES.loading ? 'none' : 'block'
            }}
            data-recommendations-infinite-scroll-trigger=""
        />
    );

    return (
        <div className="product-recommendations">
            {/* KATS: replace with ItemCardGrid */}
            <section className="item-card-layout layout-grid d-flex flex-wrap infinite-scroll">
                <ComponentsWithStitchAds
                    adsEnabled={showAds}
                    pageIndex={1}
                    components={itemComponents}
                />
            </section>
            {loadingState === LOADING_STATES.loading && loader}
            {loadingState === LOADING_STATES.empty && errorFallback}
            {loadingState === LOADING_STATES.end && noMoreItemsMarker}
            {loadingState !== LOADING_STATES.end &&
                loadingState !== LOADING_STATES.empty &&
                loadingTrigger}
        </div>
    );
};
